import {
  useEffect,
  useId,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';
import {
  ChartBarIcon,
  ClockCounterClockwiseIcon,
  DatabaseIcon,
  LightningIcon,
  type Icon,
  FlowArrowIcon,
  FolderIcon,
  PlusIcon,
  SidebarSimpleIcon,
  TableIcon,
  TrayArrowUpIcon,
} from '@phosphor-icons/react';
import { IconTooltip } from '@/components/common/IconTooltip';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { T, useGT } from 'gt-react';
import type { WorkbookOutputKind } from '@erp/shared-types';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import type { WorkbookEditor } from './useWorkbookEditor';
import type { PendingUpload } from './useWorkbookUploads';
import { processOutline } from '@erp/workbooks';
import { WorkbookExecutionIcon } from './WorkbookExecutionLabel';
import { useExecutionLabel } from './useExecutionLabel';
import { useWorkbookReferences } from './useWorkbookReferences';
import { WorkbookItemIcon } from './workbookItemIcon';
import {
  workbookItemIconKind,
  type WorkbookItemIconKind,
} from './workbookItemIconKind';
import { hasActiveRun } from './useWorkbookQueries';
import {
  activeRunStep,
  formatElapsed,
  formatShortElapsed,
  useElapsed,
} from './workbookRunProgress';
import { WorkbookOutputMenu } from './WorkbookOutputMenu';
import { WorkbookSheetMenu } from './WorkbookSheetMenu';
import { WorkbookSourceMenu } from './WorkbookSourceMenu';
import { removeWorkbookItem } from './workbookAgentEdits';
import { WorkbookAddSheetMenu } from './WorkbookAddSheetMenu';
import { SHEET_KIND_UI } from './sheetKindUi';
import { newFreeformSheet } from './freeform/freeformEdits';
import { newWorkbookOutput } from './workbookOutputs';

/**
 * Scrolls the Process view to a node once it shows. Each call is a new
 * request, so asking for the same node again scrolls to it again.
 */
function useProcessNodeFocus(selected: string): (id: string) => void {
  const [request, setRequest] = useState<{ id: string } | null>(null);
  const scrolled = useRef<{ id: string } | null>(null);
  useEffect(() => {
    if (selected !== 'process' || !request || scrolled.current === request)
      return;
    scrolled.current = request;
    document
      .getElementById(`process-node-${request.id}`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [selected, request]);
  return (id) => setRequest({ id });
}

// Inherited complexity: this change adds one prop and an onDoubleClick (no branches).
// fallow-ignore-next-line complexity
export function WorkbookNavigation({
  editor,
  selected,
  opened,
  setSelected,
  previewItem,
  keepItem,
  busy,
  canUpdate,
  pendingUploads = [],
}: {
  editor: WorkbookEditor;
  selected: string;
  /** The ids of the open tabs. */
  opened: readonly string[];
  setSelected: (id: string) => void;
  /** Shows an item in the temporary tab, as a click on it in the list does. */
  previewItem: (id: string) => void;
  /** Keeps an item's tab open, as a double click on it in the list does. */
  keepItem: (id: string) => void;
  busy: boolean;
  canUpdate: boolean;
  /** Files being uploaded, each shown at once as a pending item. */
  pendingUploads?: readonly PendingUpload[];
}) {
  const gt = useGT();
  const references = useWorkbookReferences(editor.definition, setSelected);
  const { label, cellTitle } = useExecutionLabel(references);
  const [view, setView] = useState<'assets' | 'process'>('assets');
  const [collapsed, setCollapsed] = useState(false);
  const expand = () => setCollapsed(false);
  const focusProcessNode = useProcessNodeFocus(selected);
  const { definition } = editor;
  const running = hasActiveRun(editor.runs.data);
  const step = activeRunStep(editor.runs.data);
  const elapsed = useElapsed(step?.startedAt);
  /** How long the running step has taken, on the item it runs: its Worksheet, else Runs. */
  const stepElapsed = (itemId: string) =>
    (step?.kind === 'sheet' ? step.sheetId : 'runs') === itemId
      ? elapsed
      : undefined;
  // While a Run reads its data, each Data item pulses.
  const loadingData =
    step?.kind === 'sources'
      ? gt('Loading data', { context: 'workbook data item status' })
      : undefined;
  const runsTab = {
    id: 'runs',
    name: gt('Runs', { context: 'workbook runs nav' }),
    icon: ClockCounterClockwiseIcon,
    // Pulses while a Run is queued or running, so it shows from any tab.
    active: running
      ? step?.kind === 'sources'
        ? gt('Reading data sources', { context: 'workbook runs nav status' })
        : step?.kind === 'saving'
          ? gt('Saving results', { context: 'workbook runs nav status' })
          : gt('A run is in progress', { context: 'workbook runs nav status' })
      : undefined,
  };
  const automationsTab = {
    id: 'automations',
    name: gt('Automations', { context: 'workbook automations nav' }),
    icon: LightningIcon,
  };
  /**
   * A Process view click: the item's own tab when it is open, else the
   * Process tab scrolled to the node.
   */
  function showInProcess(nodeId: string, itemId?: string) {
    if (itemId !== undefined && opened.includes(itemId)) {
      setSelected(itemId);
      return;
    }
    setSelected('process');
    focusProcessNode(nodeId);
  }
  function addOutput(kind: WorkbookOutputKind) {
    const id = crypto.randomUUID();
    const name =
      kind === 'file'
        ? gt('Export', { context: 'workbook new file output name' })
        : kind === 'sink'
          ? gt('Data sink', { context: 'workbook new data sink output name' })
          : kind === 'app'
            ? gt('App', { context: 'workbook new app output name' })
            : gt('Report', { context: 'workbook new report output name' });
    editor.edits.workbook.update((latest) => ({
      ...latest,
      outputs: [...latest.outputs, newWorkbookOutput(latest, kind, name, id)],
    }));
    setSelected(id);
  }
  function addFreeform() {
    const id = crypto.randomUUID();
    const base = gt('Freeform sheet', {
      context: 'workbook new freeform sheet name',
    });
    editor.edits.workbook.update((latest) => ({
      ...latest,
      sheets: [...latest.sheets, newFreeformSheet(latest, id, base)],
    }));
    setSelected(id);
  }
  function navItem(item: {
    id: string;
    name: string;
    folder?: string;
    icon?: Icon;
    /** Something in progress, which the icon pulses for. */
    active?: string;
  }) {
    const kind = references.resolve(item.id).kind;
    const itemTypeLabel =
      kind === 'file'
        ? gt('File', { context: 'workbook data item type' })
        : kind === 'source'
          ? gt('Data source', { context: 'workbook data item type' })
          : undefined;
    return (
      <CollapsedTooltip key={item.id} collapsed={collapsed} label={item.name}>
        <button
          type="button"
          className={`relative flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs outline-offset-[-2px] focus-visible:outline-2 focus-visible:outline-ring ${selected === item.id ? 'bg-primary/10 text-primary' : 'hover:bg-muted'}`}
          onClick={() => previewItem(item.id)}
          onDoubleClick={() => keepItem(item.id)}
        >
          <NavItemIcon
            icon={item.icon}
            active={Boolean(item.active)}
            kind={workbookItemIconKind(definition, item.id) ?? 'worksheet'}
            label={itemTypeLabel}
          />
          <span className={`truncate ${FADE}`}>
            {item.folder ? `${item.folder} / ` : ''}
            {item.name}
          </span>
          <StepElapsed
            elapsed={stepElapsed(item.id)}
            inline={item.id === 'runs'}
          />
          {item.active && <span className="sr-only">{item.active}</span>}
        </button>
      </CollapsedTooltip>
    );
  }
  const addOutputMenu = (
    <DropdownMenu>
      <IconTooltip
        label={gt('Add output', { context: 'workbook add output menu' })}
      >
        <DropdownMenuTrigger asChild>
          <Button
            size="icon"
            variant="ghost"
            className="size-6"
            disabled={busy || !canUpdate}
            aria-label={gt('Add output', {
              context: 'workbook add output menu',
            })}
          >
            <PlusIcon className="size-3.5" />
          </Button>
        </DropdownMenuTrigger>
      </IconTooltip>
      <DropdownMenuContent align="start">
        <DropdownMenuItem onClick={() => addOutput('report')}>
          <ChartBarIcon />
          <T context="workbook add output">Report</T>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => addOutput('file')}>
          <WorkbookItemIcon kind="xlsx" />
          <T context="workbook add output">File</T>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => addOutput('sink')}>
          <WorkbookItemIcon kind="sink" />
          <T context="workbook add output">Data sink</T>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => addOutput('app')}>
          <WorkbookItemIcon kind="app" />
          <T context="workbook add output">App</T>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
  const addWorksheet = gt('Add worksheet', {
    context: 'workbook add worksheet',
  });
  const addWorksheetMenu = (
    <WorkbookAddSheetMenu
      label={addWorksheet}
      disabled={busy || !canUpdate}
      onAdd={(kind) => {
        const tab = SHEET_KIND_UI[kind].addTab;
        if (tab) setSelected(tab);
        else addFreeform();
      }}
    />
  );
  /** The Outputs section, the same in both views. */
  function outputsSection(children: ReactNode) {
    return (
      <NavigationSection
        title={<T context="workbook outputs section">Outputs</T>}
        label={gt('Outputs', { context: 'workbook outputs section' })}
        icon={TrayArrowUpIcon}
        collapsed={collapsed}
        onExpand={expand}
        action={addOutputMenu}
      >
        {children}
      </NavigationSection>
    );
  }
  return (
    <aside
      data-collapsed={collapsed || undefined}
      className="group/nav flex w-52 shrink-0 flex-col overflow-clip border-r bg-muted/20 transition-[width] duration-200 ease-out data-[collapsed]:w-11 data-[collapsed]:delay-150 motion-reduce:transition-none"
    >
      <NavigationHeader
        collapsed={collapsed}
        toggle={() => setCollapsed((current) => !current)}
        view={view}
        setView={(next) => {
          setView(next);
          if (next === 'process') setSelected('process');
        }}
        showProcess={() => setSelected('process')}
      />
      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto overflow-x-clip p-2">
        {view === 'assets' && (
          <>
            <NavigationSection
              title={<T context="workbook data nav">Data</T>}
              label={gt('Data', { context: 'workbook data nav' })}
              icon={DatabaseIcon}
              collapsed={collapsed}
              onExpand={expand}
              action={
                <IconTooltip
                  label={gt('Add data', { context: 'workbook add data menu' })}
                >
                  <Button
                    size="icon"
                    variant="ghost"
                    className="size-6"
                    disabled={busy || !canUpdate}
                    aria-label={gt('Add data', {
                      context: 'workbook add data menu',
                    })}
                    onClick={() => setSelected('add-input')}
                  >
                    <PlusIcon className="size-3.5" />
                  </Button>
                </IconTooltip>
              }
            >
              {definition.files.map((item) =>
                navItem({ ...item, active: loadingData }),
              )}
              {pendingUploads.map((upload) => (
                <div
                  key={upload.id}
                  aria-busy="true"
                  aria-disabled="true"
                  title={upload.name}
                  className="pointer-events-none flex w-full select-none items-center gap-2 rounded px-2 py-1.5 text-xs text-muted-foreground opacity-60"
                >
                  <WorkbookItemIcon
                    kind="xlsx"
                    className="size-3.5 shrink-0"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 truncate">{upload.name}</span>
                  <Skeleton className="h-3 w-8 shrink-0" />
                </div>
              ))}
              {definition.sources.map((item) => (
                <div key={item.id} className="group relative">
                  {navItem({ ...item, active: loadingData })}
                  {canUpdate && (
                    <WorkbookSourceMenu
                      name={item.name}
                      disabled={busy}
                      onRemove={() => {
                        editor.edits.workbook.update((latest) =>
                          removeWorkbookItem(latest, 'sources', item.id),
                        );
                        if (selected === item.id) setSelected('process');
                      }}
                    />
                  )}
                </div>
              ))}
            </NavigationSection>

            <NavigationSection
              title={<T context="workbook transforms nav">Worksheets</T>}
              label={gt('Worksheets', { context: 'workbook transforms nav' })}
              icon={TableIcon}
              collapsed={collapsed}
              onExpand={expand}
              action={addWorksheetMenu}
            >
              {definition.sheets.map((item) => (
                <div key={item.id} className="group relative">
                  {navItem(item)}
                  {canUpdate && (
                    <WorkbookSheetMenu
                      sheet={item}
                      definition={definition}
                      disabled={busy}
                      onDelete={() => {
                        editor.edits.workbook.update((latest) =>
                          removeWorkbookItem(latest, 'sheets', item.id),
                        );
                        if (selected === item.id) setSelected('process');
                      }}
                    />
                  )}
                </div>
              ))}
            </NavigationSection>
            {outputsSection(
              <>
                {definition.outputs.map((output) => (
                  <div
                    key={output.id}
                    className="group flex items-center gap-0.5"
                  >
                    <div className="min-w-0 flex-1">{navItem(output)}</div>
                    {canUpdate && (
                      <WorkbookOutputMenu
                        output={output}
                        edits={editor.edits}
                        disabled={busy}
                      />
                    )}
                  </div>
                ))}
              </>,
            )}
          </>
        )}
        {view === 'process' && (
          <>
            <NavigationSection
              title={
                <T context="workbook execution order">Calculation order</T>
              }
              label={gt('Calculation order', {
                context: 'workbook execution order',
              })}
              icon={FlowArrowIcon}
              collapsed={collapsed}
              onExpand={expand}
              action={addWorksheetMenu}
            >
              {processOutline(definition)
                // A cell's first Instruction titles its row, so it is not listed again.
                .cells.flatMap((cell) => [
                  { node: cell.head, text: cellTitle(cell) },
                  ...cell.instructions
                    .slice(1)
                    .map((node) => ({ node, text: label(node) })),
                ])
                .map(({ node, text }) => (
                  <button
                    key={node.id}
                    type="button"
                    className="relative flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted outline-offset-[-2px] focus-visible:outline-2 focus-visible:outline-ring"
                    style={{ paddingLeft: 8 + node.depth * 16 }}
                    title={text}
                    onClick={() =>
                      showInProcess(
                        node.id,
                        node.kind === 'sheet' ? node.itemId : undefined,
                      )
                    }
                  >
                    <WorkbookExecutionIcon
                      node={node}
                      definition={definition}
                    />
                    <span className="truncate">{text}</span>
                    {node.kind === 'sheet' && (
                      <StepElapsed
                        elapsed={stepElapsed(node.itemId)}
                        inline={false}
                      />
                    )}
                  </button>
                ))}
            </NavigationSection>
            {/* After the Worksheets: Outputs read their Results and compute none. */}
            {outputsSection(
              <>
                {definition.outputs.map((output) => (
                  <button
                    key={output.id}
                    type="button"
                    className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted outline-offset-[-2px] focus-visible:outline-2 focus-visible:outline-ring"
                    title={output.name}
                    onClick={() => showInProcess(output.id, output.id)}
                  >
                    <WorkbookItemIcon
                      kind={
                        workbookItemIconKind(definition, output.id) ?? 'xlsx'
                      }
                      className="size-4 shrink-0 text-muted-foreground"
                      aria-hidden
                    />
                    <span className="truncate">{output.name}</span>
                  </button>
                ))}
              </>,
            )}
          </>
        )}
      </div>
      <div className="shrink-0 border-t p-2">
        {navItem(runsTab)}
        {navItem(automationsTab)}
      </div>
    </aside>
  );
}

/**
 * Fades a sidebar label out first when the sidebar collapses, and back in
 * once it has widened again when it expands.
 */
const FADE =
  'whitespace-nowrap transition-opacity duration-150 delay-200 group-data-[collapsed]/nav:pointer-events-none group-data-[collapsed]/nav:opacity-0 group-data-[collapsed]/nav:delay-0 motion-reduce:transition-none';

/** The sidebar's collapse toggle and its Assets / Process view switch. */
function NavigationHeader({
  collapsed,
  toggle,
  view,
  setView,
  showProcess,
}: {
  collapsed: boolean;
  toggle: () => void;
  view: 'assets' | 'process';
  setView: (view: 'assets' | 'process') => void;
  /** Shows the Process tab, as picking the Process view again does. */
  showProcess: () => void;
}) {
  const gt = useGT();
  return (
    <div className="flex h-12 shrink-0 items-center gap-1 border-b px-2">
      <IconTooltip>
        <Button
          size="icon"
          variant="ghost"
          className="size-7 shrink-0"
          aria-expanded={!collapsed}
          aria-label={
            collapsed
              ? gt('Expand sidebar', { context: 'workbook sidebar toggle' })
              : gt('Collapse sidebar', { context: 'workbook sidebar toggle' })
          }
          onClick={toggle}
        >
          <SidebarSimpleIcon className="size-4" />
        </Button>
      </IconTooltip>
      <ToggleGroup
        inert={collapsed}
        type="single"
        value={view}
        size="sm"
        className={`min-w-0 flex-1 bg-muted/60 p-0.5 [&>*:not(:last-child)]:border-r-0 ${FADE}`}
        aria-label={gt('Workbook navigation view', {
          context: 'workbook navigation modes',
        })}
        onValueChange={(next) => {
          if (next === 'assets' || next === 'process') setView(next);
        }}
      >
        <ToggleGroupItem
          value="assets"
          className="h-7 rounded-sm text-xs data-[pressed]:bg-background data-[pressed]:shadow-sm"
        >
          <FolderIcon />
          <T context="workbook assets view">Assets</T>
        </ToggleGroupItem>
        <ToggleGroupItem
          value="process"
          onClick={() => {
            if (view === 'process') showProcess();
          }}
          className="h-7 rounded-sm text-xs data-[pressed]:bg-background data-[pressed]:shadow-sm"
        >
          <FlowArrowIcon />
          <T context="workbook process">Process</T>
        </ToggleGroupItem>
      </ToggleGroup>
    </div>
  );
}

/**
 * How long a Run's step has been running on the item it runs: a thick bar over
 * the section's line and the time in the gutter beside it, leaving the row as is.
 */
function StepElapsed({
  elapsed,
  inline,
}: {
  elapsed: number | undefined;
  /** The item has no section line beside it (Runs): the time ends its row instead. */
  inline: boolean;
}) {
  const gt = useGT();
  if (elapsed === undefined) return null;
  const label = (
    <span className="sr-only">
      {gt('Running for {elapsed}', {
        context: 'workbook running step elapsed',
        elapsed: formatElapsed(elapsed),
      })}
    </span>
  );
  if (inline)
    return (
      <>
        <span
          className={`ml-auto shrink-0 text-[10px] text-primary tabular-nums ${FADE}`}
          aria-hidden
        >
          {formatElapsed(elapsed)}
        </span>
        {label}
      </>
    );
  return (
    <>
      <span
        className="absolute inset-y-0.5 -left-1.5 w-[3px] rounded-full bg-primary"
        aria-hidden
      />
      <span
        className={`absolute right-[calc(100%+0.5rem)] text-[10px] leading-none text-primary tabular-nums ${FADE}`}
        aria-hidden
      >
        {formatShortElapsed(elapsed)}
      </span>
      {label}
    </>
  );
}

/** A sidebar item's own icon, or its kind's; pulsing while `active`. */
function NavItemIcon({
  icon: ItemIcon,
  active,
  kind,
  label,
}: {
  icon?: Icon;
  active: boolean;
  kind: WorkbookItemIconKind;
  /** The item's type, read out when it says more than the name. */
  label?: string;
}) {
  if (ItemIcon)
    return (
      <ItemIcon
        className={`size-3.5 shrink-0 ${active ? 'animate-pulse text-primary motion-reduce:animate-none' : 'text-muted-foreground'}`}
        aria-hidden
      />
    );
  return (
    <WorkbookItemIcon
      kind={kind}
      className={`size-3.5 shrink-0 ${active ? 'animate-pulse text-primary motion-reduce:animate-none' : 'text-muted-foreground'}`}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    />
  );
}

/** Names an item on hover while the sidebar is collapsed to its icons. */
function CollapsedTooltip({
  collapsed,
  label,
  children,
}: {
  collapsed: boolean;
  label: string;
  children: ReactElement;
}) {
  if (!collapsed) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

function NavigationSection({
  title,
  label,
  icon: SectionIcon,
  collapsed,
  onExpand,
  action,
  children,
}: {
  title: ReactNode;
  /** The title as text, for the collapsed sidebar's icon. */
  label: string;
  icon: Icon;
  collapsed: boolean;
  /** Expands the sidebar, as a click on the collapsed section's icon does. */
  onExpand: () => void;
  action: ReactNode;
  children: ReactNode;
}) {
  const titleId = useId();
  const items = useRef<HTMLDivElement>(null);
  const actions = useRef<HTMLDivElement>(null);
  // Set by the collapsed icon: the icon gives way to the section's title once
  // the sidebar expands, so focus moves into the section it opened (its first
  // item, else its add button) instead of falling back to the page.
  const focusOnExpand = useRef(false);
  useEffect(() => {
    if (collapsed || !focusOnExpand.current) return;
    focusOnExpand.current = false;
    const target =
      items.current?.querySelector<HTMLElement>('button:not(:disabled)') ??
      actions.current?.querySelector<HTMLElement>('button:not(:disabled)');
    target?.focus();
  }, [collapsed]);
  function expandInto() {
    focusOnExpand.current = true;
    onExpand();
  }
  return (
    <section aria-labelledby={titleId}>
      <div className="flex items-center gap-1">
        {collapsed ? (
          <CollapsedTooltip collapsed label={label}>
            <Button
              size="icon"
              variant="ghost"
              className="size-7 shrink-0 text-muted-foreground"
              aria-label={label}
              onClick={expandInto}
            >
              <SectionIcon className="size-4" />
            </Button>
          </CollapsedTooltip>
        ) : (
          <span className="flex size-7 shrink-0 items-center justify-center text-muted-foreground">
            <SectionIcon className="size-4" aria-hidden />
          </span>
        )}
        <h3
          id={titleId}
          className={`min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground ${FADE}`}
        >
          {title}
        </h3>
        <div ref={actions} inert={collapsed} className={FADE}>
          {action}
        </div>
      </div>
      {/* Children close before the sidebar narrows, and open once it has widened. */}
      <div
        inert={collapsed}
        className="grid grid-rows-[1fr] transition-[grid-template-rows,opacity] delay-200 duration-150 ease-out group-data-[collapsed]/nav:grid-rows-[0fr] group-data-[collapsed]/nav:opacity-0 group-data-[collapsed]/nav:delay-0 motion-reduce:transition-none"
      >
        {/* Clips at the section's edge, so a running item's time shows in the gutter left of the line. */}
        <div ref={items} className="-ml-2 min-h-0 overflow-hidden pl-2">
          <div className="ml-3.5 border-l pl-1">{children}</div>
        </div>
      </div>
    </section>
  );
}
